import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import API_BASE_URL from '../config';
import './GuardiasPage.css';

const fmtTiempoEnCorral = (llegadaAt) => {
  if (!llegadaAt) return '—';
  const ms = Date.now() - new Date(llegadaAt).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}min`;
};

// Hora HH:mm CDMX
const fmtHoraCDMX = (isoOrDate) => {
  if (!isoOrDate) return '—';
  const d = new Date(isoOrDate);
  return d.toLocaleTimeString('es-MX', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Mexico_City',
  });
};

// "3h 24min" · "42min" · "—"
const fmtDuracion = (ms) => {
  if (ms == null || ms <= 0) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  return `${h}h ${String(min % 60).padStart(2, '0')}min`;
};

// Clasifica el tiempo restante para color: <1h => urgente, 1-2h => aviso, >2h => ok
const claseUrgencia = (ms) => {
  if (ms == null) return 'gp-time-neutral';
  const min = ms / 60000;
  if (min < 60) return 'gp-time-urgent';
  if (min < 120) return 'gp-time-warn';
  return 'gp-time-ok';
};

const GuardiasPage = () => {
  const token = useSelector((state) => state.auth.token);
  const decoded = useMemo(() => { try { return jwtDecode(token); } catch { return null; } }, [token]);
  const estacion = decoded?.estacion || '';

  const [posiciones, setPosiciones] = useState([]);
  const [miAsignacion, setMiAsignacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPosicion, setSelectedPosicion] = useState(null);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [tick, setTick] = useState(0);

  const authHdr = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}api/guardias/dashboard`, authHdr);
      setPosiciones(data.resumen || []);
      setMiAsignacion(data.miAsignacion || null);
    } catch (err) {
      console.error('Error cargando guardias:', err.response?.status || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authHdr]);

  // Primera carga con spinner
  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresco silencioso cada 20s (sin spinner ni flicker)
  useEffect(() => {
    const t = setInterval(() => {
      fetchData({ silent: true });
      setTick((x) => x + 1);
    }, 20_000);
    return () => clearInterval(t);
  }, [fetchData]);

  const soyCoordinadorTitular = (posicion) =>
    miAsignacion && miAsignacion.tipo === 'COORDINADOR' &&
    String(miAsignacion.posicionId) === String(posicion._id);

  const handleAsumirCoordinacion = async (posicionId) => {
    try {
      await axios.post(`${API_BASE_URL}api/guardias/asignar-coordinador`, { posicionId }, authHdr);
      await fetchData();
      setSelectedPosicion(null);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo asumir.');
    }
  };

  if (loading && posiciones.length === 0) {
    return <div className="gp-container"><div className="gp-loading">Cargando guardias…</div></div>;
  }

  const sinMiAsignacion = !miAsignacion;
  const esCoordinador = decoded?.rol === 'colaborador';

  return (
    <div className="gp-container">
      <header className="gp-header">
        <div>
          <h1 className="gp-title">Guardias</h1>
          <p className="gp-subtitle">Estación {estacion} · {posiciones.length} posiciones</p>
        </div>
        <div className="gp-header-actions">
          {esCoordinador && (
            <button className="gp-btn gp-btn-primary" onClick={() => setShowCrearModal(true)}>
              <i className="fa fa-plus" /> Nueva posición
            </button>
          )}
        </div>
      </header>

      {esCoordinador && sinMiAsignacion && (
        <div className="gp-banner">
          <i className="fa fa-info-circle" />
          <span>Aún no tienes una posición asignada. Selecciona una libre para asumir la coordinación.</span>
        </div>
      )}

      {miAsignacion && (
        <div className="gp-mi-posicion">
          <i className="fa fa-user-shield" />
          <span>
            Estás asignado a <strong>{miAsignacion.posicionNombre}</strong> como {miAsignacion.tipo === 'COORDINADOR' ? 'coordinador' : 'operador'}
            {miAsignacion.enAlimentos && <span className="gp-tag-alimentos"> · En alimentos</span>}
          </span>
        </div>
      )}

      <div className="gp-grid">
        {posiciones.map((p) => {
          const esMia = soyCoordinadorTitular(p);
          const sinCoordinador = !p.coordinador;
          const operadores = p.operadores || [];
          const pasajeros = p.pasajeros || [];
          return (
            <div
              key={p._id}
              className={`gp-card ${esMia ? 'gp-card-mine' : ''} ${sinCoordinador ? 'gp-card-libre' : ''}`}
              onClick={() => setSelectedPosicion(p)}
            >
              <div className="gp-card-header">
                <h3>{p.nombre}</h3>
                <div className="gp-card-header-badges">
                  {sinCoordinador && <span className="gp-badge-libre">Libre</span>}
                  <span className="gp-cupo">{p.cupoOcupado}/{p.cupoTotal}</span>
                </div>
              </div>

              <div className="gp-card-coord">
                {p.coordinador ? (
                  <>
                    <i className="fa fa-user-shield" />
                    <span className="gp-coord-name">{p.coordinador.nombre}</span>
                    {p.coordinador.enAlimentos && <span className="gp-chip gp-chip-warn">Alimentos</span>}
                  </>
                ) : (
                  <span className="gp-sin-coord">Sin coordinador</span>
                )}
              </div>

              {/* Operadores */}
              <div className="gp-card-section">
                <div className="gp-card-section-head">
                  <i className="fa fa-users" /> Operadores
                  <span className="gp-count">{operadores.length}/{p.cupoTotal}</span>
                </div>
                {operadores.length === 0 ? (
                  <div className="gp-card-empty">Sin operadores</div>
                ) : (
                  <ul className="gp-card-list">
                    {operadores.slice(0, 4).map((op) => (
                      <li key={op._id}>
                        <span className="gp-card-list-name">{op.nombre}</span>
                        {op.siglas && <span className="gp-card-list-siglas">{op.siglas}</span>}
                        <span className="gp-card-list-sub">#{op.noColaborador}</span>
                      </li>
                    ))}
                    {operadores.length > 4 && (
                      <li className="gp-card-list-more">+{operadores.length - 4} más</li>
                    )}
                  </ul>
                )}
              </div>

              {/* Pasajeros en posición */}
              <div className="gp-card-section">
                <div className="gp-card-section-head">
                  <i className="fa fa-user-clock" /> Pasajeros en posición <strong>{p.nombre}</strong>
                  <span className="gp-count">{pasajeros.length}</span>
                </div>
                {pasajeros.length === 0 ? (
                  <div className="gp-card-empty">Sin pasajeros</div>
                ) : (
                  <ul className="gp-card-list gp-card-list-pax">
                    {pasajeros.slice(0, 3).map((s) => (
                      <li key={s._id}>
                        <span className="gp-card-list-name">
                          {`${s.nombrePasajero || ''} ${s.apellidoPasajero || ''}`.trim() || `Vuelo ${s.noVuelo}`}
                        </span>
                        <span className="gp-card-list-sub">
                          {fmtTiempoEnCorral(s.llegadaGuardiaAt)} · {s.tipoSilla || 'WCHR'}
                        </span>
                      </li>
                    ))}
                    {pasajeros.length > 3 && (
                      <li className="gp-card-list-more">+{pasajeros.length - 3} más</li>
                    )}
                  </ul>
                )}
              </div>

              {sinCoordinador && esCoordinador && !miAsignacion && (
                <button
                  className="gp-card-asumir"
                  onClick={(e) => { e.stopPropagation(); handleAsumirCoordinacion(p._id); }}
                >
                  Asumir coordinación
                </button>
              )}
            </div>
          );
        })}
      </div>

      {selectedPosicion && (
        <PosicionDetalleModal
          posicionId={selectedPosicion._id}
          onClose={() => setSelectedPosicion(null)}
          onChange={() => fetchData({ silent: true })}
          onCloseAfterChange={async () => {
            setSelectedPosicion(null);
            await fetchData();
          }}
          miAsignacion={miAsignacion}
          decoded={decoded}
          authHdr={authHdr}
        />
      )}

      {showCrearModal && (
        <CrearPosicionModal
          onClose={() => setShowCrearModal(false)}
          onCreated={async () => { setShowCrearModal(false); await fetchData(); }}
          authHdr={authHdr}
        />
      )}

      {/* tick oculto para que los tiempos en corral se actualicen */}
      <span style={{ display: 'none' }}>{tick}</span>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// Modal detalle
// ═════════════════════════════════════════════════════════════════════════
const PosicionDetalleModal = ({ posicionId, onClose, onChange, onCloseAfterChange, miAsignacion, decoded, authHdr }) => {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [asignarPickerOpen, setAsignarPickerOpen] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState(null);

  const fetchDetalle = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}api/guardias/posiciones/${posicionId}`, authHdr);
      setDetalle(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [posicionId, authHdr]);

  useEffect(() => { fetchDetalle(); }, [fetchDetalle]);

  const soyTitular =
    miAsignacion?.tipo === 'COORDINADOR' &&
    String(miAsignacion.posicionId) === String(posicionId);

  const refrescar = async () => { await fetchDetalle(); onChange(); };

  const handleAsignarBulk = async (noColaboradores) => {
    if (!noColaboradores.length) return { ok: 0, fallidos: 0 };
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}api/guardias/asignar-operadores`, {
        posicionId, operadores: noColaboradores,
      }, authHdr);
      await refrescar();
      return data;
    } catch (err) {
      // 409 con conflictos parciales todavía viene con body válido
      if (err.response?.data?.asignados) {
        await refrescar();
        return err.response.data;
      }
      throw new Error(err.response?.data?.error || err.message || 'No se pudo asignar.');
    } finally { setBusy(false); }
  };

  const quitarAsignacion = async (asignacionId) => {
    if (!window.confirm('¿Quitar esta asignación?')) return;
    try {
      await axios.delete(`${API_BASE_URL}api/guardias/asignaciones/${asignacionId}`, authHdr);
      await refrescar();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error desconocido.');
    }
  };

  const reportarNoShow = async (asignacionId) => {
    const motivo = window.prompt('Motivo (opcional):') || null;
    try {
      await axios.post(`${API_BASE_URL}api/guardias/reportar-no-show`, { asignacionId, motivo }, authHdr);
      setNoShowTarget(null);
      await refrescar();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error desconocido.');
    }
  };

  const cambiarCupoExtra = async (delta) => {
    const nuevo = Math.max(0, (detalle.cupoExtra || 0) + delta);
    try {
      await axios.patch(`${API_BASE_URL}api/guardias/posiciones/${posicionId}`, { cupoExtra: nuevo }, authHdr);
      await refrescar();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error desconocido.');
    }
  };

  const toggleAlimentos = async () => {
    try {
      await axios.patch(`${API_BASE_URL}api/guardias/alimentos`, {}, authHdr);
      await refrescar();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error desconocido.');
    }
  };

  const liberarCoordinacion = async () => {
    if (!window.confirm('¿Liberar tu posición?')) return;
    try {
      await axios.post(`${API_BASE_URL}api/guardias/liberar-coordinador`, {}, authHdr);
      onCloseAfterChange();   // cierra el modal y refresca la lista
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error desconocido.');
    }
  };

  // ── Estado del reasignador ──
  const [reasignarTarget, setReasignarTarget] = useState(null); // servicio a reasignar
  const [pickerLleno, setPickerLleno] = useState(false);
  const [bitacoraOps, setBitacoraOps] = useState([]);
  const [busquedaOp, setBusquedaOp] = useState('');
  const [reasigBusy, setReasigBusy] = useState(false);

  const abrirReasignar = (servicio) => {
    setReasignarTarget(servicio);
    setPickerLleno(false);
    setBusquedaOp('');
    setBitacoraOps([]);
  };

  const confirmarReasignacion = async (noColaboradorOp) => {
    if (!reasignarTarget) return;
    setReasigBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}api/guardias/corral/${reasignarTarget._id}/recoger`,
        { noColaboradorOperador: Number(noColaboradorOp) },
        authHdr,
      );
      setReasignarTarget(null);
      await refrescar();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error.');
    } finally {
      setReasigBusy(false);
    }
  };

  const cargarOperadoresBitacora = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, authHdr);
      const estUser = (decoded?.estacion || '').toUpperCase();
      const disponibles = (res.data || []).filter(
        (c) => c.estacion?.trim().toUpperCase() === estUser,
      );
      setBitacoraOps(disponibles);
      setPickerLleno(true);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error al cargar operadores.');
    }
  };

  if (loading || !detalle) {
    return (
      <div className="gp-modal-overlay" onClick={onClose}>
        <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
          <div className="gp-modal-body"><div className="gp-loading">Cargando…</div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-modal-overlay" onClick={onClose}>
      <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
        <header className="gp-modal-header">
          <h3>{detalle.nombre}</h3>
          <button className="gp-modal-close" onClick={onClose}>✕</button>
        </header>

        <div className="gp-modal-body">
          {/* Coordinador */}
          <section className="gp-section">
            <h4>Coordinador</h4>
            {detalle.coordinador ? (
              <div className="gp-coord-row">
                <div>
                  <strong>{detalle.coordinador.nombre}</strong>
                  {detalle.coordinador.enAlimentos && <span className="gp-chip gp-chip-warn">Alimentos</span>}
                </div>
                {soyTitular && (
                  <div className="gp-actions-inline">
                    <button className="gp-btn gp-btn-ghost" onClick={toggleAlimentos}>
                      {detalle.coordinador.enAlimentos ? 'Regresar de alimentos' : 'Marcar alimentos'}
                    </button>
                    <button className="gp-btn gp-btn-danger" onClick={liberarCoordinacion}>Liberar</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="gp-empty">Sin coordinador asignado.</div>
            )}
          </section>

          {/* Operadores */}
          <section className="gp-section">
            <div className="gp-section-head">
              <h4>Operadores de guardia ({detalle.cupoOcupado}/{detalle.cupoTotal})</h4>
              {soyTitular && (
                <div className="gp-actions-inline">
                  <button className="gp-btn gp-btn-small" onClick={() => cambiarCupoExtra(-1)} disabled={!detalle.cupoExtra}>−</button>
                  <span className="gp-extra-label">+{detalle.cupoExtra || 0} extras</span>
                  <button className="gp-btn gp-btn-small" onClick={() => cambiarCupoExtra(1)}>+</button>
                </div>
              )}
            </div>

            {detalle.operadores.length === 0 ? (
              <div className="gp-empty">Sin operadores asignados.</div>
            ) : (
              <ul className="gp-op-list">
                {detalle.operadores.map((op) => (
                  <li key={op._id} className="gp-op-row">
                    <div>
                      <strong>{op.nombre}{op.siglas ? ` · ${op.siglas}` : ''}</strong>
                      <span className="gp-op-sub">#{op.noColaborador}</span>
                    </div>
                    {soyTitular && (
                      <div className="gp-actions-inline">
                        <button className="gp-btn gp-btn-ghost gp-btn-small" onClick={() => reportarNoShow(op._id)}>
                          <i className="fa fa-user-slash" /> No-show
                        </button>
                        <button className="gp-btn gp-btn-danger gp-btn-small" onClick={() => quitarAsignacion(op._id)}>
                          Quitar
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {soyTitular && detalle.cupoLibre > 0 && (
              <div className="gp-add-op-cta">
                <button
                  className="gp-btn gp-btn-primary"
                  onClick={() => setAsignarPickerOpen(true)}
                  disabled={busy}
                >
                  <i className="fa fa-user-plus" /> Cargar operadores en rol
                </button>
                <span className="gp-add-op-hint">
                  {detalle.cupoLibre} {detalle.cupoLibre === 1 ? 'lugar libre' : 'lugares libres'}
                </span>
              </div>
            )}
          </section>

          {/* Pasajeros en la posición */}
          <section className="gp-section">
            <h4>Pasajeros en {detalle.nombre} ({detalle.pasajerosCorral.length})</h4>
            {detalle.pasajerosCorral.length === 0 ? (
              <div className="gp-empty">Sin pasajeros en esta posición.</div>
            ) : (
              <ul className="gp-pax-list">
                {detalle.pasajerosCorral.map((s) => (
                  <li key={s._id} className="gp-pax-row">
                    <div>
                      <strong>{`${s.nombrePasajero || ''} ${s.apellidoPasajero || ''}`.trim() || `Vuelo ${s.noVuelo}`}</strong>
                      <span className="gp-op-sub">
                        Vuelo {s.noVuelo || '—'} · {s.tipoSilla || 'WCHR'}
                      </span>
                      <div className="gp-pax-meta">
                        Dejado por {s.dejadoEnGuardiaPor?.nombre || '—'} · {fmtTiempoEnCorral(s.llegadaGuardiaAt)} esperando
                      </div>
                    </div>
                    {soyTitular && (
                      <button className="gp-btn gp-btn-primary gp-btn-small" onClick={() => abrirReasignar(s)}>
                        Reasignar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* ── Sub-modal picker: asignar operadores en rol ─────── */}
      {asignarPickerOpen && (
        <AsignarOperadoresPickerModal
          posicionNombre={detalle.nombre}
          cupoLibre={detalle.cupoLibre}
          onClose={() => setAsignarPickerOpen(false)}
          onAsignar={handleAsignarBulk}
          authHdr={authHdr}
          decoded={decoded}
        />
      )}

      {/* ── Sub-modal de reasignación ─────────────────────────── */}
      {reasignarTarget && (
        <div className="gp-modal-overlay" onClick={() => !reasigBusy && setReasignarTarget(null)}>
          <div className="gp-modal gp-modal-small" onClick={(e) => e.stopPropagation()}>
            <header className="gp-modal-header">
              <h3>Reasignar pasajero</h3>
              <button className="gp-modal-close" onClick={() => setReasignarTarget(null)} disabled={reasigBusy}>✕</button>
            </header>
            <div className="gp-modal-body">
              <div className="gp-reasig-info">
                <strong>
                  {`${reasignarTarget.nombrePasajero || ''} ${reasignarTarget.apellidoPasajero || ''}`.trim() ||
                    `Vuelo ${reasignarTarget.noVuelo}`}
                </strong>
                <span className="gp-op-sub">Vuelo {reasignarTarget.noVuelo || '—'} · {reasignarTarget.tipoSilla || 'WCHR'}</span>
              </div>

              {!pickerLleno ? (
                <>
                  {/* Operadores de guardia aquí (rápido) */}
                  <section className="gp-section">
                    <h4>Operadores de guardia en {detalle.nombre}</h4>
                    {detalle.operadores.length === 0 ? (
                      <div className="gp-empty">No hay operadores de guardia aquí.</div>
                    ) : (
                      <ul className="gp-op-list">
                        {detalle.operadores.map((op) => (
                          <li key={op._id} className="gp-op-row">
                            <div>
                              <strong>{op.nombre}{op.siglas ? ` · ${op.siglas}` : ''}</strong>
                              <span className="gp-op-sub">#{op.noColaborador}</span>
                            </div>
                            <button
                              className="gp-btn gp-btn-primary gp-btn-small"
                              onClick={() => confirmarReasignacion(op.noColaborador)}
                              disabled={reasigBusy}
                            >
                              {reasigBusy ? '…' : 'Asignar'}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <div style={{ textAlign: 'center', margin: '6px 0' }}>
                    <button className="gp-btn gp-btn-ghost" onClick={cargarOperadoresBitacora} disabled={reasigBusy}>
                      Elegir otro operador (bitácora)
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Picker completo de bitácora */}
                  <section className="gp-section">
                    <div className="gp-section-head">
                      <h4>Operadores En Turno ({bitacoraOps.length})</h4>
                      <button className="gp-btn gp-btn-ghost gp-btn-small" onClick={() => setPickerLleno(false)}>
                        ← Volver
                      </button>
                    </div>
                    <input
                      type="text"
                      className="gp-picker-search"
                      placeholder="Buscar por siglas, nombre o número…"
                      value={busquedaOp}
                      onChange={(e) => setBusquedaOp(e.target.value.toUpperCase())}
                    />
                    <ul className="gp-op-list gp-picker-list">
                      {bitacoraOps
                        .filter((c) => {
                          if (!busquedaOp) return true;
                          const hay = [c.siglas, c.nombreCompleto, String(c.noColaborador)]
                            .filter(Boolean).join(' ').toUpperCase();
                          return hay.includes(busquedaOp);
                        })
                        .map((c) => (
                          <li key={c.noColaborador} className="gp-op-row">
                            <div>
                              <strong>{c.nombreCompleto}</strong>
                              <span className="gp-op-sub">#{c.noColaborador} · {c.siglas || '—'}</span>
                            </div>
                            <button
                              className="gp-btn gp-btn-primary gp-btn-small"
                              onClick={() => confirmarReasignacion(c.noColaborador)}
                              disabled={reasigBusy}
                            >
                              {reasigBusy ? '…' : 'Asignar'}
                            </button>
                          </li>
                        ))}
                    </ul>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// Modal crear posición
// ═════════════════════════════════════════════════════════════════════════
const CrearPosicionModal = ({ onClose, onCreated, authHdr }) => {
  const [nombre, setNombre] = useState('');
  const [cupo, setCupo] = useState(3);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE_URL}api/guardias/posiciones`, {
        nombre: nombre.trim(), cupoOperadores: Number(cupo) || 0,
      }, authHdr);
      onCreated();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo crear.');
    } finally { setBusy(false); }
  };

  return (
    <div className="gp-modal-overlay" onClick={onClose}>
      <div className="gp-modal gp-modal-small" onClick={(e) => e.stopPropagation()}>
        <header className="gp-modal-header">
          <h3>Nueva posición</h3>
          <button className="gp-modal-close" onClick={onClose}>✕</button>
        </header>
        <form className="gp-modal-body" onSubmit={submit}>
          <label className="gp-field">
            <span>Nombre</span>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. 82 SALIDAS"
              autoFocus
            />
          </label>
          <label className="gp-field">
            <span>Cupo de operadores (default)</span>
            <input type="number" min="0" value={cupo} onChange={(e) => setCupo(e.target.value)} />
          </label>
          <div className="gp-modal-actions">
            <button type="button" className="gp-btn gp-btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="gp-btn gp-btn-primary" disabled={busy || !nombre.trim()}>
              {busy ? 'Creando…' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// Sub-modal: Cargar operadores en rol → seleccionar múltiples → asignar
// ═════════════════════════════════════════════════════════════════════════
const AsignarOperadoresPickerModal = ({ posicionNombre, cupoLibre, onClose, onAsignar, authHdr, decoded }) => {
  const [loading, setLoading] = useState(true);
  const [operadores, setOperadores] = useState([]);
  const [seleccion, setSeleccion] = useState(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [tick, setTick] = useState(0);
  const [asignando, setAsignando] = useState(false);
  const [resumen, setResumen] = useState(null);

  const estUser = (decoded?.estacion || '').trim().toUpperCase();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, authHdr);
        if (!alive) return;
        const filtrados = (data || []).filter((c) => (c.estacion || '').trim().toUpperCase() === estUser);
        setOperadores(filtrados);
      } catch (err) {
        console.error('Cargar en-turno:', err.response?.status || err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authHdr, estUser]);

  // tick para refrescar tiempos restantes cada 60s
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const now = Date.now();
  const q = busqueda.trim().toUpperCase();

  const rows = useMemo(() => {
    return operadores
      .map((c) => {
        const salidaAt = c.turnoFin ? new Date(c.turnoFin) : null;
        const restanteMs = salidaAt ? salidaAt.getTime() - now : null;
        const key = String(c.noColaborador);
        const hay = [c.siglas, c.nombreCompleto, key].filter(Boolean).join(' ').toUpperCase();
        return {
          key,
          noColaborador: c.noColaborador,
          nombreCompleto: c.nombreCompleto,
          siglas: c.siglas,
          salidaAt,
          restanteMs,
          match: q ? hay.includes(q) : true,
        };
      })
      .filter((r) => r.match)
      .sort((a, b) => (a.restanteMs || 0) - (b.restanteMs || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operadores, q, tick]);

  const toggle = (nocol) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      const k = String(nocol);
      if (next.has(k)) next.delete(k);
      else {
        if (next.size >= cupoLibre) return prev;  // cap a cupoLibre
        next.add(k);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (seleccion.size >= Math.min(rows.length, cupoLibre)) {
      setSeleccion(new Set());
    } else {
      const next = new Set();
      for (const r of rows) {
        if (next.size >= cupoLibre) break;
        next.add(String(r.noColaborador));
      }
      setSeleccion(next);
    }
  };

  const confirmar = async () => {
    if (!seleccion.size) return;
    setAsignando(true);
    setResumen(null);
    try {
      const nums = Array.from(seleccion).map((s) => Number(s));
      const r = await onAsignar(nums);
      setResumen(r);
      if (r?.conflictos?.length === 0) {
        setTimeout(() => onClose(), 900);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setAsignando(false);
    }
  };

  return (
    <div className="gp-modal-overlay" onClick={() => !asignando && onClose()}>
      <div className="gp-modal gp-asig-modal" onClick={(e) => e.stopPropagation()}>
        <header className="gp-modal-header">
          <div>
            <h3>Asignar operadores</h3>
            <p className="gp-asig-sub">
              Posición <strong>{posicionNombre}</strong> · {cupoLibre} {cupoLibre === 1 ? 'lugar libre' : 'lugares libres'}
            </p>
          </div>
          <button className="gp-modal-close" onClick={onClose} disabled={asignando}>✕</button>
        </header>

        <div className="gp-asig-toolbar">
          <div className="gp-asig-search">
            <i className="fa fa-search" />
            <input
              type="text"
              placeholder="Buscar por nombre, siglas o número…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoFocus
            />
            {busqueda && (
              <button className="gp-asig-clear" onClick={() => setBusqueda('')} title="Limpiar">✕</button>
            )}
          </div>
          <button
            className="gp-btn gp-btn-ghost gp-btn-small"
            onClick={toggleAll}
            disabled={loading || !rows.length}
          >
            {seleccion.size >= Math.min(rows.length, cupoLibre) ? 'Deseleccionar' : `Seleccionar ${Math.min(rows.length, cupoLibre)}`}
          </button>
        </div>

        <div className="gp-asig-list" role="listbox" aria-multiselectable="true">
          {loading ? (
            <div className="gp-asig-skel">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="gp-asig-skel-row" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="gp-asig-empty">
              <i className="fa fa-users-slash" />
              <p>{q ? 'Ningún operador coincide con la búsqueda.' : 'No hay operadores en turno en esta estación.'}</p>
              {q && <button className="gp-btn gp-btn-ghost gp-btn-small" onClick={() => setBusqueda('')}>Limpiar búsqueda</button>}
            </div>
          ) : (
            rows.map((r) => {
              const sel = seleccion.has(r.key);
              const capFull = !sel && seleccion.size >= cupoLibre;
              return (
                <label
                  key={r.key}
                  className={`gp-asig-row ${sel ? 'gp-asig-row-sel' : ''} ${capFull ? 'gp-asig-row-dis' : ''}`}
                  aria-selected={sel}
                >
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => toggle(r.noColaborador)}
                    disabled={capFull}
                  />
                  <div className="gp-asig-row-main">
                    <div className="gp-asig-row-name">
                      <strong>{r.nombreCompleto || `#${r.noColaborador}`}</strong>
                      {r.siglas && <span className="gp-asig-row-sigla">{r.siglas}</span>}
                    </div>
                    <div className="gp-asig-row-meta">
                      <span className="gp-asig-num">#{r.noColaborador}</span>
                      {r.salidaAt && (
                        <>
                          <span className="gp-asig-sep">·</span>
                          <span className="gp-asig-out">
                            Sale <strong>{fmtHoraCDMX(r.salidaAt)}</strong>
                          </span>
                          <span className={`gp-asig-time ${claseUrgencia(r.restanteMs)}`}>
                            {r.restanteMs > 0 ? `restan ${fmtDuracion(r.restanteMs)}` : 'turno terminado'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        {resumen && (
          <div className={`gp-asig-resumen ${resumen.conflictos?.length ? 'gp-asig-resumen-warn' : 'gp-asig-resumen-ok'}`}>
            <div>
              <strong>{resumen.resumen?.ok || 0}</strong> asignados
              {resumen.conflictos?.length ? <> · <strong>{resumen.conflictos.length}</strong> con conflicto</> : ''}
            </div>
            {!!resumen.conflictos?.length && (
              <ul>
                {resumen.conflictos.slice(0, 5).map((c) => (
                  <li key={c.noColaborador}>#{c.noColaborador} — {c.error}</li>
                ))}
                {resumen.conflictos.length > 5 && <li>+{resumen.conflictos.length - 5} más…</li>}
              </ul>
            )}
          </div>
        )}

        <footer className="gp-asig-footer">
          <div className="gp-asig-count">
            <strong>{seleccion.size}</strong> de {cupoLibre} seleccionados
          </div>
          <div className="gp-asig-actions">
            <button className="gp-btn gp-btn-ghost" onClick={onClose} disabled={asignando}>Cancelar</button>
            <button
              className="gp-btn gp-btn-primary"
              onClick={confirmar}
              disabled={!seleccion.size || asignando}
            >
              {asignando ? 'Asignando…' : (
                seleccion.size ? `Asignar ${seleccion.size}` : 'Asignar'
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default GuardiasPage;
